import axios from 'axios';
import { deviceHeaders, isLoginRequest } from './deviceId';
import { isCredentialCall } from './credentialCalls';

// Use relative URL if REACT_APP_BACKEND_URL is empty or not set
// This allows the frontend to work from any domain (local IP or DDNS)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // D-SS10 (17/09/2026): a sign-in attempt carries what this browser can
    // honestly say about itself — Misty's own device label, the screen size,
    // the language and the timezone — so the Login Attempts report can tell one
    // computer from another. Only on the four sign-in routes: there is no
    // reason for the label to travel on every request in the program.
    try {
      if (isLoginRequest(config.url)) {
        config.headers = { ...(config.headers || {}), ...deviceHeaders() };
      }
    } catch (_e) { /* never let this stop a login going out */ }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Coerce any FastAPI / Pydantic / network error shape into a flat human
// string. Without this, calling toast.error(err.friendlyMessage) on
// a Pydantic 422 (where `detail` is an array of {type, loc, msg, input}
// objects) crashes the toast renderer with "Objects are not valid as a
// React child". Importable for explicit use; also attached to every
// rejected error as `err.friendlyMessage` by the interceptor below.
export const friendlyApiError = (err, fallback = 'Something went wrong') => {
  if (!err) return fallback;
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => {
        if (!e || typeof e !== 'object') return String(e);
        const where = Array.isArray(e.loc) && e.loc.length ? ` (${e.loc.join('.')})` : '';
        return `${e.msg || e.message || 'validation error'}${where}`;
      })
      .join('; ');
  }
  if (d && typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  // D-SEC10 (14/09/2026): the per-computer rate limiter answers with
  // {error: "..."} rather than {detail: "..."}, and slowapi prefixes its own
  // jargon to the plain sentence the route set. Strip the prefix and show the
  // sentence, so the 2FA screens say "too many attempts" instead of falling
  // back to "Invalid code" and blaming the person for the six digits.
  const limited = err?.response?.status === 429 ? err?.response?.data?.error : null;
  if (typeof limited === 'string' && limited) {
    return limited.replace(/^Rate limit exceeded:\s*/i, '');
  }
  return err?.response?.data?.message || err?.message || fallback;
};

// D-SEC10 (14/09/2026) — ONE refresh at a time, however many requests met the
// expired token. The interceptor below answers every 401 by refreshing, and a
// screen that fires a dozen requests at once (the roll-label lookups on a
// grouped raw material are one per stock entry, with no cap) used to produce a
// dozen separate POSTs to /auth/refresh in the same second. That was wasteful
// before; now that the route allows thirty a minute it is the one way an honest
// person could trip the limit. Everyone waits on the first refresh instead, so
// one expiry costs exactly one call no matter how many requests were in flight.
// D-SS4 (17/09/2026) — the sentences the backend sends with a 401 when the
// session has been ended on purpose (auth.py: ACCOUNT_NO_LONGER_ACTIVE_MESSAGE,
// ACCESS_CHANGED_MESSAGE, and the 2FA set-up lock, which D-SS4 moved into the
// per-request check so it now arrives mid-shift rather than only at login).
// Matched exactly rather than guessed at, so an ordinary expired-token 401
// still shows the plain login screen.
export const SESSION_ENDED_MESSAGES = [
  'Your account is no longer active. Ask an admin.',
  'Your access has changed. Please sign in again.',
  'Two-factor authentication was not set up after 3 reminders, so this account is locked. '
    + 'Ask an admin to unlock it in Staff & Security.',
];

// D-SS4 (17/09/2026) — adopt a fresh pair of tokens handed back by a route
// that deliberately ended every session (changing your own password, switching
// your own 2FA off). Those routes bump `token_epoch`, which kills the token in
// THIS browser as well, so they return a replacement pair and this stores it.
// Without it the person would be signed out by their own click.
export const adoptFreshTokens = (payload) => {
  const access = payload?.access_token;
  if (!access) return false;
  try {
    localStorage.setItem('token', access);
    if (payload.refresh_token) localStorage.setItem('refreshToken', payload.refresh_token);
  } catch (_e) { /* blocked storage: the in-memory header below still works */ }
  api.defaults.headers.common.Authorization = `Bearer ${access}`;
  return true;
};

// D-SS3 (17/09/2026) — the ONE refusal this toast is for. Anything else that
// answers 403 is left alone: the role gates say "Insufficient permissions",
// which is a developer sentence, and some of those calls have always been made
// by people the route was never going to accept (the sidebar asks for the Xero
// status on every page load). Toasting those would put an error in front of
// ordinary staff on every screen, for something that has worked this way for
// years — and it would do it whatever SS3_ENFORCE is set to.
export const NO_ACCESS_MESSAGE =
  'You do not have access to this. Ask an admin to add it in Staff & Security.';

// When each message was last shown. A map rather than one "last message" slot,
// because two different refusals arriving alternately beat a single slot every
// time; and a minute rather than five seconds, because the screens that poll do
// so every thirty.
const forbiddenShownAt = new Map();
const FORBIDDEN_QUIET_MS = 60000;

// D-SS4 (17/09/2026) — the two two-factor routes a person meets BEFORE they
// have a session, the password and the manager PIN now live together in
// `utils/credentialCalls.js` (`isCredentialCall`), where the rule can be
// tested on its own.

let refreshInFlight = null;

export const refreshAccessTokenOnce = (refreshTok) => {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post(`/auth/refresh?refresh_token=${encodeURIComponent(refreshTok)}`)
      .then(({ data }) => {
        const newToken = data && data.access_token;
        if (!newToken) throw new Error('no access_token in refresh response');
        localStorage.setItem('token', newToken);
        if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
        return newToken;
      })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Blob/file downloads that fail hand back error.response.data as a Blob,
    // so the server's JSON `detail` is hidden and callers see a generic
    // message. Parse it back into JSON *before* friendlyApiError and the 5xx
    // logger below so both surface the real detail. Never let this throw.
    try {
      const rd = error.response?.data;
      if (rd && typeof Blob !== 'undefined' && rd instanceof Blob &&
          (!rd.type || rd.type.includes('json') || rd.type.includes('text'))) {
        const text = await rd.text();
        try { error.response.data = JSON.parse(text); }
        catch (_p) { if (text) error.response.data = { detail: text }; }
      }
    } catch (_b) { /* keep the original error on any parse failure */ }
    // Attach a guaranteed-string error message every caller can rely on.
    // Catch blocks should `toast.error(err.friendlyMessage || 'fallback')`
    // rather than touching `err.response.data.detail` directly.
    try { error.friendlyMessage = friendlyApiError(error); } catch (_) { /* never raise */ }
    const status = error.response?.status;
    if (status === 401) {
      // Don't auto-redirect on 401s from the auth endpoints themselves —
      // wrong-password / bad-2FA-code / failed-refresh need to bubble up
      // so the Login UI can show a toast. Hard-reloading the page on every
      // failed login attempt makes it impossible to actually log in.
      // Every 401 that is somebody typing a credential IN — a password, a
      // six-digit code, a manager PIN — rather than a session running out.
      // Neither replayed after a token refresh nor a reason to sign anybody
      // out; the screen that asked shows the server's sentence instead.
      const isAuthCall = isCredentialCall(error.config?.url || '');

      const originalRequest = error.config || {};
      const doLogout = () => {
        const hadToken = !!localStorage.getItem('token');
        // D-SS4 (17/09/2026): when the server says WHY the session ended —
        // "Your account is no longer active. Ask an admin." or "Your access
        // has changed. Please sign in again." — carry that sentence to the
        // login screen instead of dropping the person at a bare login box with
        // no idea what happened. It goes through localStorage because the line
        // below hard-navigates, which throws away React state.
        try {
          const detail = error.response?.data?.detail;
          if (typeof detail === 'string' && SESSION_ENDED_MESSAGES.includes(detail)) {
            localStorage.setItem('logoutNotice', detail);
          }
        } catch (_n) { /* a full or blocked localStorage must not stop the logout */ }
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sessionStart');
        // Only hard-nav if we actually had a session — otherwise we're on
        // the login screen already and another reload looks like a loop.
        if (hadToken) window.location.href = '/';
      };
      const refreshTok = localStorage.getItem('refreshToken');
      if (!isAuthCall && refreshTok && !originalRequest._retry) {
        // Try the refresh-token flow before dropping the session. _retry
        // guards against an infinite 401->refresh->401 loop; isAuthCall
        // already excludes /auth/refresh so we never refresh the refresh.
        originalRequest._retry = true;
        return refreshAccessTokenOnce(refreshTok)
          .then((newToken) => {
            api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          })
          .catch((refreshError) => {
            // D-SEC10 (14/09/2026): /auth/refresh is rate limited now, so a
            // failure here is no longer proof the session is dead. A 429 means
            // "you asked too often" — the refresh token is still perfectly
            // good, often for weeks — so throwing the person out at that point
            // would lose their unsaved work and make them sign in AND type a
            // 2FA code, which is itself limited. Only a real refusal logs out.
            if (refreshError?.response?.status !== 429) doLogout();
            return Promise.reject(error);
          });
      }
      if (!isAuthCall) doLogout();
    }
    // D-SS3 (17/09/2026): the menus already hide a screen somebody has no tick
    // for. What was missing is what happens when a 403 arrives anyway — a
    // bookmarked address, a screen left open while an admin changed a tick, a
    // background poll. Say the server's sentence once, rather than leaving a
    // screen silently empty.
    if (status === 403 && error.response?.data?.detail === NO_ACCESS_MESSAGE) {
      const now = Date.now();
      const lastShown = forbiddenShownAt.get(NO_ACCESS_MESSAGE) || 0;
      if (now - lastShown > FORBIDDEN_QUIET_MS) {
        forbiddenShownAt.set(NO_ACCESS_MESSAGE, now);
        import('sonner').then(({ toast }) => toast.error(NO_ACCESS_MESSAGE)).catch(() => {});
      }
    }
    // Auto-log server errors (5xx) to the Issues log. Dynamic import so
    // we don't create a circular require at module load — api.js is a
    // hot path and errorLogger also uses fetch, not axios, so there's
    // no real cycle, but the dynamic import keeps this file lean.
    if (status && status >= 500) {
      import('./errorLogger').then(({ logError }) => {
        try {
          const cfg = error.config || {};
          const serverDetail = error.response?.data?.detail || error.response?.statusText || 'Server error';
          logError({
            severity: 'critical',
            origin: 'api_error',
            message: `API ${cfg.method ? cfg.method.toUpperCase() : ''} ${cfg.url || ''} → ${status}`,
            details: typeof serverDetail === 'string' ? serverDetail : JSON.stringify(serverDetail).slice(0, 500),
            context: {
              method: cfg.method,
              url: cfg.url,
              status,
              response_preview: typeof error.response?.data === 'object'
                ? JSON.stringify(error.response.data).slice(0, 500)
                : String(error.response?.data || '').slice(0, 500),
            },
          });
        } catch (_e) { /* never raise */ }
      }).catch(() => { /* logger missing is survivable */ });
    }
    return Promise.reject(error);
  }
);

// API helper functions
export const apiHelpers = {
  // Authentication
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),

  // Desktop app (Electron client) installers
  getDesktopClientInfo: () => api.get('/desktop-client/info'),

  // Clients
  getClients: () => api.get('/clients'),
  // The customer DROPDOWN: an id, a company name and an active flag, and
  // nothing else (D-SS3B (c), Callum 18/09/2026). Every screen that only needs
  // to put customer names in a list uses this one, so that un-ticking Clients
  // for somebody really does take the customer record — bank details, payment
  // terms and prices — away from them. `getClients` above returns the whole
  // record and belongs to the Clients screen and the screens behind its tick.
  getClientNames: () => api.get('/clients/names'),
  createClient: (data) => api.post('/clients', data),
  updateClient: (id, data) => api.put(`/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/clients/${id}`),
  getClient: (id) => api.get(`/clients/${id}`),
  uploadClientLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/clients/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Products
  getProducts: () => api.get('/products'),
  getClientProducts: (clientId) => api.get(`/clients/${clientId}/products`),
  createProduct: (data) => api.post('/products', data),
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  getProduct: (id) => api.get(`/products/${id}`),
  getComparativePricing: (coreId, coreWidth) => api.get(`/products/comparative-pricing?core_id=${coreId}&core_width=${coreWidth}`),
  
  // Materials
  getMaterials: () => api.get('/materials'),
  createMaterial: (data) => api.post('/materials', data),
  updateMaterial: (id, data) => api.put(`/materials/${id}`, data),
  getMaterial: (id) => api.get(`/materials/${id}`),
  deleteMaterial: (id) => api.delete(`/materials/${id}`),
  
  // Cartons (WP-MC) — the boxes cores are packed in. Until this package a
  // carton was only a number on a product; it is now a record with a name, a
  // size and a quantity on hand.
  getCartons: (includeInactive = false) =>
    api.get(`/cartons${includeInactive ? '?include_inactive=true' : ''}`),
  createCarton: (data) => api.post('/cartons', data),
  updateCarton: (id, data) => api.put(`/cartons/${id}`, data),
  retireCarton: (id) => api.delete(`/cartons/${id}`),
  adjustCartonStock: (id, data) => api.post(`/cartons/${id}/adjust`, data),
  setFinishingCarton: (orderId, data) => api.post(`/orders/${orderId}/finishing-carton`, data),
  // `acceptShortage` is the packer saying "use it anyway" when the standard
  // box's count on the shelf is short — the same answer choosing a box gives.
  // WP-SR: `itemIndex` is the part of a split order the boxes belong to. Null
  // is a single-line order, which is stored and answered exactly as before.
  clearFinishingCarton: (orderId, acceptShortage = false, itemIndex = null) => {
    const query = [];
    if (acceptShortage) query.push('accept_shortage=true');
    if (itemIndex != null) query.push(`item_index=${encodeURIComponent(itemIndex)}`);
    return api.delete(`/orders/${orderId}/finishing-carton${query.length ? `?${query.join('&')}` : ''}`);
  },
  getOrderCartonHistory: (orderId, itemIndex = null) =>
    api.get(`/orders/${orderId}/carton-history${itemIndex != null ? `?item_index=${encodeURIComponent(itemIndex)}` : ''}`),
  getPendingCartonSubstitutions: () => api.get('/carton-substitutions/pending'),
  approveCartonSubstitution: (id, data) => api.post(`/carton-substitutions/${id}/approve`, data),
  queryCartonSubstitution: (id, data) => api.post(`/carton-substitutions/${id}/query`, data),
  setProductStandardCarton: (productId, data) =>
    api.post(`/client-products/${productId}/standard-carton`, data),

  // Suppliers  
  getSuppliers: () => api.get('/suppliers'),
  createSupplier: (data) => api.post('/suppliers', data),
  updateSupplier: (id, data) => api.put(`/suppliers/${id}`, data),
  getSupplier: (id) => api.get(`/suppliers/${id}`),
  deleteSupplier: (id) => api.delete(`/suppliers/${id}`),
  
  // Product Specifications
  getProductSpecifications: () => api.get('/product-specifications'),
  createProductSpecification: (data) => api.post('/product-specifications', data),
  updateProductSpecification: (id, data) => api.put(`/product-specifications/${id}`, data),
  getProductSpecification: (id) => api.get(`/product-specifications/${id}`),
  deleteProductSpecification: (id) => api.delete(`/product-specifications/${id}`),
  syncProductSpecToClientProducts: (specId) => api.post('/sync/product-spec-to-client-products', null, { params: { spec_id: specId } }),
  syncClientProductToSpec: (clientId, productId) => api.post('/sync/client-product-to-spec', null, { params: { client_id: clientId, product_id: productId } }),
  
  // Calculators
  calculateMaterialConsumptionByClient: (data) => api.post('/calculators/material-consumption-by-client', data),
  calculateMaterialPermutation: (data) => api.post('/calculators/material-permutation', data),
  calculateSpiralCoreConsumption: (data) => api.post('/calculators/spiral-core-consumption', data),
  
  // Stocktake
  getCurrentStocktake: () => api.get('/stocktake/current'),
  createStocktake: (data) => api.post('/stocktake', data),
  updateStocktakeEntry: (stocktakeId, data) => api.put(`/stocktake/${stocktakeId}/entry`, data),
  completeStocktake: (stocktakeId) => api.post(`/stocktake/${stocktakeId}/complete`),
  
  // Client Product Catalogue
  getClientCatalogue: (clientId) => api.get(`/clients/${clientId}/catalog`),
  getClientProduct: (clientId, productId) => api.get(`/clients/${clientId}/catalog/${productId}`),
  createClientProduct: (clientId, data) => api.post(`/clients/${clientId}/catalog`, data),
  updateClientProduct: (clientId, productId, data) => api.put(`/clients/${clientId}/catalog/${productId}`, data),
  deleteClientProduct: (clientId, productId) => api.delete(`/clients/${clientId}/catalog/${productId}`),
  copyClientProduct: (clientId, productId, targetClientId) => api.post(`/clients/${clientId}/catalog/${productId}/copy-to/${targetClientId}`),
  getAllClientProducts: () => api.get('/client-products/all'),
  
  // Orders
  getOrders: (statusFilter) => api.get('/orders', { params: { status_filter: statusFilter } }),
  createOrder: (data) => api.post('/orders', data),
  getOrder: (id) => api.get(`/orders/${id}`),
  deleteOrder: (id) => api.delete(`/orders/${id}`),
  updateOrder: (id, data) => api.put(`/orders/${id}`, data),
  updateOrderStage: (id, data) => api.put(`/orders/${id}/stage`, data),

  // Order drafts from ChatGPT (WP-GPT2). A draft is not an order: it waits on
  // the Orders screen until somebody with Orders access converts or discards it.
  getOrderDrafts: (status) => api.get('/order-drafts', { params: status ? { status } : {} }),
  convertOrderDraft: (draftId) => api.post(`/order-drafts/${draftId}/convert`),
  discardOrderDraft: (draftId) => api.post(`/order-drafts/${draftId}/discard`),
  
  // Job Specifications
  createJobSpecification: (data) => api.post('/job-specifications', data),
  getJobSpecificationByOrder: (orderId) => api.get(`/orders/${orderId}/job-specification`),
  
  // Production Board
  getProductionBoard: () => api.get('/production/board'),
  getProductionLogs: (orderId) => api.get(`/production/logs/${orderId}`),
  moveOrderStage: (orderId, data) => api.post(`/production/move-stage/${orderId}`, data),
  jumpToStage: (orderId, data) => api.post(`/production/jump-stage/${orderId}`, data),
  reorderJobs: (data) => api.put('/orders/reorder', data),
  getMaterialsStatus: (orderId) => api.get(`/production/materials-status/${orderId}`),
  updateMaterialsStatus: (orderId, data) => api.put(`/production/materials-status/${orderId}`, data),
  updateOrderItemStatus: (orderId, data) => api.put(`/production/order-item-status/${orderId}`, data),
  
  // Jobs
  getJob: (jobId) => api.get(`/jobs/${jobId}`),
  // WP-AJ: the Archived Jobs page on a customer. `params` carries the page's
  // own filters — { period } for the archived orders, { period, process } for
  // the saved run sheets — so the server does the filtering, not the screen.
  getClientArchivedJobs: (clientId, params) => api.get(`/clients/${clientId}/archived-jobs`, { params }),
  getClientJobCards: (clientId, params) => api.get(`/clients/${clientId}/job-cards`, { params }),
  saveJobCard: (data) => api.post('/production/job-cards', data),
  getJobCardsByOrder: (orderId) => api.get(`/production/job-cards/order/${orderId}`),
  updateJobCard: (jobCardId, data) => api.put(`/production/job-cards/${jobCardId}`, data),
  searchJobCards: (params) => api.get('/production/job-cards/search', { params }),
  
  // Customer Stock Holdings & Consignment
  getCustomerStockHoldings: (clientId) => api.get('/customer-stock-holdings', { params: clientId ? { client_id: clientId } : {} }),
  getCustomerStockHolding: (holdingId) => api.get(`/customer-stock-holdings/${holdingId}`),
  createCustomerStockHolding: (data) => api.post('/customer-stock-holdings', data),
  uploadStockHoldingExcel: (holdingId, file, supplierName = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (supplierName) {
      formData.append('supplier_name', supplierName);
    }
    return api.post(`/customer-stock-holdings/${holdingId}/upload-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addManualPallet: (holdingId, palletData) => api.post(`/customer-stock-holdings/${holdingId}/add-pallet`, palletData),
  getAvailablePallets: (holdingId) => api.get(`/customer-stock-holdings/${holdingId}/available-pallets`),
  getClientAvailableStock: (clientId) => api.get(`/customer-stock-holdings/by-client/${clientId}/available`),
  deliverPallets: (data) => api.post('/customer-stock-holdings/deliver-pallets', data),
  scanPalletQR: (holdingId, qrData, action) => api.post(`/customer-stock-holdings/${holdingId}/scan-pallet?action=${action}`, qrData),
  // One-shot fast-path for the Barcode Scanner instant-print flow.
  // Collapses assignment lookup + label-data build + print + mark-printed
  // into a single round-trip. Returns { success, use_browser_print, ... }.
  scanPrintPalletLabel: (holdingId, palletId) => api.post(`/customer-stock-holdings/${holdingId}/scan-print/${palletId}`),
  // One-shot finishing-stage carton label printer. Returns
  // { success, use_browser_print, message } — when use_browser_print is
  // true, the caller should fall back to the modal-based print flow.
  finishingPrintLabel: (orderId, data) => api.post(`/orders/${orderId}/finishing-print-label`, data),
  deleteCustomerStockHolding: (holdingId) => api.delete(`/customer-stock-holdings/${holdingId}`),
  getClientDefaultDeliveryCharge: (clientId) => api.get(`/clients/${clientId}/default-delivery-charge`),
  // D-SS3E (b): the customer's short-order threshold and charge, and nothing
  // else off the record, so the order form can show the short-order note to
  // anybody who may raise an order.
  getClientOrderTerms: (clientId) => api.get(`/clients/${clientId}/order-terms`),
  bulkUpdatePalletSupplier: (holdingId, data) => api.post(`/customer-stock-holdings/${holdingId}/bulk-update-supplier`, data),
  
  // Pallet status management
  markPalletsPrinted: (holdingId, palletIds) => api.post(`/customer-stock-holdings/${holdingId}/mark-pallets-printed`, { pallet_ids: palletIds }),
  markPalletsForDelivery: (holdingId, palletIds, deliveryRequestId) => api.post(`/customer-stock-holdings/${holdingId}/mark-pallets-for-delivery`, { pallet_ids: palletIds, delivery_request_id: deliveryRequestId }),
  archivePallets: (holdingId, palletIds, deliveryDate) => api.post(`/customer-stock-holdings/${holdingId}/archive-pallets`, { pallet_ids: palletIds, delivery_date: deliveryDate }),
  manuallyArchivePallet: (holdingId, palletId, reason) => api.post(`/stock-holdings/manual-archive`, { holding_id: holdingId, pallet_id: palletId, reason }),
  reconcileStock: (clientId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);
    return api.post('/stock-holdings/reconcile', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  reconcileAction: (holdingId, palletId, action, reason) => api.post('/stock-holdings/reconcile-action', { holding_id: holdingId, pallet_id: palletId, action, reason }),
  getReplacementPalletOptions: (holdingId, palletId) => api.get(`/stock-holdings/${holdingId}/replacement-options/${palletId}`),
  replacePalletInDeliveryRequest: (requestId, data) => api.post(`/customer-delivery-requests/${requestId}/replace-pallet`, data),
  restorePallets: (holdingId, palletIds) => api.post(`/customer-stock-holdings/${holdingId}/restore-pallets`, { pallet_ids: palletIds }),
  getArchivedPallets: (holdingId) => api.get(`/customer-stock-holdings/${holdingId}/archived-pallets`),
  exportArchivedPallets: (holdingId) => api.get('/customer-stock-holdings/export-archived', { params: holdingId ? { holding_id: holdingId } : {} }),
  
  // Reports
  getOutstandingJobsReport: () => api.get('/reports/outstanding-jobs'),
  getLateDeliveriesReport: () => api.get('/reports/late-deliveries'),
  getCustomerAnnualReport: (clientId, year) => api.get(`/reports/customer-annual/${clientId}`, { params: { year } }),
  getCustomerAnnualReportDetailed: (clientId, fromYear, operatorRate) => api.get(`/reports/customer-annual-detailed/${clientId}`, { params: { from_year: fromYear || 0, operator_rate: operatorRate || 0 } }),
  getTrialBalanceReport: () => api.get('/reports/trial-balance'),
  getDetailedMaterialUsageReport: (materialId, startDate, endDate, includeOrderBreakdown) => 
    api.get('/stock/reports/material-usage-detailed', { 
      params: { 
        material_id: materialId, 
        start_date: startDate, 
        end_date: endDate,
        include_order_breakdown: includeOrderBreakdown
      } 
    }),
  getAllMaterialsUsageReport: (startDate, endDate, includeOrderBreakdown) =>
    api.get('/stock/reports/all-materials-usage', {
      params: {
        start_date: startDate,
        end_date: endDate,
        include_order_breakdown: includeOrderBreakdown
      }
    }),
  // Material usage — two reliable options
  getTotalMaterialUsedReport: (startDate, endDate) =>
    api.get('/stock/reports/total-material-used', { params: { start_date: startDate, end_date: endDate } }),
  getMaterialOrderProjection: () =>
    api.get('/stock/reports/material-order-projection'),
  getOrderProjections: () =>
    api.get('/reports/order-projections'),
  snoozeOrderProjection: (clientId, productId, days = 14) =>
    api.post('/order-projections/snooze', { client_id: clientId, product_id: productId, days }),
  getMaterialInvoicedReport: (startDate, endDate) =>
    api.get('/stock/reports/material-invoiced', { params: { start_date: startDate, end_date: endDate } }),
  exportTotalMaterialUsedCSV: (startDate, endDate) =>
    api.get('/stock/reports/total-material-used/export-csv', { params: { start_date: startDate, end_date: endDate }, responseType: 'blob' }),
  exportMaterialInvoicedCSV: (startDate, endDate) =>
    api.get('/stock/reports/material-invoiced/export-csv', { params: { start_date: startDate, end_date: endDate }, responseType: 'blob' }),
  getReceiptsReport: (startDate, endDate, clientId) =>
    api.get('/stock/reports/receipts', { params: { start_date: startDate, end_date: endDate, client_id: clientId || undefined } }),
  exportReceiptsCSV: (startDate, endDate, clientId) =>
    api.get('/stock/reports/receipts/export-csv', { params: { start_date: startDate, end_date: endDate, client_id: clientId || undefined }, responseType: 'blob' }),
  getDetailedProductUsageReport: (clientId, startDate, endDate, includeOrderBreakdown) =>
    api.get('/stock/reports/product-usage-detailed', { 
      params: { 
        client_id: clientId, 
        start_date: startDate, 
        end_date: endDate,
        include_order_breakdown: includeOrderBreakdown
      } 
    }),
  getProjectedOrderAnalysis: (clientId, startDate, endDate) => 
    api.get('/stock/reports/projected-order-analysis', { 
      params: { 
        client_id: clientId, 
        start_date: startDate, 
        end_date: endDate
      } 
    }),
  getJobCardPerformance: (startDate, endDate) => 
    api.get('/stock/reports/job-card-performance', { 
      params: { 
        start_date: startDate, 
        end_date: endDate
      } 
    }),
  exportJobCardPerformanceCSV: (startDate, endDate) => 
    api.get('/stock/reports/job-card-performance/export-csv', { 
      params: { 
        start_date: startDate, 
        end_date: endDate
      },
      responseType: 'blob'
    }),
  
  // Machine Production Report
  getMachineProductionReport: (machineId, startDate, endDate) => 
    api.get('/reports/machine-production', { 
      params: { 
        machine_id: machineId || undefined, 
        start_date: startDate, 
        end_date: endDate
      } 
    }),
  exportMachineProductionCSV: (machineId, startDate, endDate) => 
    api.get('/reports/machine-production/export-csv', { 
      params: { 
        machine_id: machineId || undefined, 
        start_date: startDate, 
        end_date: endDate
      },
      responseType: 'blob'
    }),
  
  // Documents
  generateAcknowledgment: (orderId) => api.get(`/documents/acknowledgment/${orderId}`, { responseType: 'blob' }),
  generateAcknowledgmentWithTemplate: (orderId, templateId) => api.get(`/documents/acknowledgment/${orderId}/template/${templateId}`, { responseType: 'blob' }),
  // WP-SR: the job-card PDF is built from one line of the order. `itemIndex`
  // is the part a split order's tile is for; without it the server builds
  // line 1, which on a split order is the wrong part. Null is a single-line
  // order and is asked for exactly as before.
  generateJobCard: (orderId, itemIndex = null) =>
    api.get(
      `/documents/job-card/${orderId}${itemIndex != null ? `?item_index=${encodeURIComponent(itemIndex)}` : ''}`,
      { responseType: 'blob' },
    ),
  generatePackingList: (orderId) => api.get(`/documents/packing-list/${orderId}`, { responseType: 'blob' }),
  // WP-INV2 item A4: with an invoice id this returns THAT instalment's invoice;
  // without one, the latest on the job. A job with no invoice at all now comes
  // back as a 404 carrying a plain sentence, so any caller must read
  // `detail` off the error rather than saving the body as a PDF.
  generateInvoice: (orderId, invoiceId = null) => api.get(
    `/documents/invoice/${orderId}` + (invoiceId ? `?invoice_id=${encodeURIComponent(invoiceId)}` : ''),
    { responseType: 'blob' }
  ),
  
  // Invoicing
  getLiveJobs: () => api.get('/invoicing/live-jobs'),
  getArchivedJobs: (month, year) => api.get('/invoicing/archived-jobs', { params: { month, year } }),
  generateJobInvoice: (jobId, invoiceData) => api.post(`/invoicing/generate/${jobId}`, invoiceData),
  // WP-GP: ONE invoice covering every job of a customer order. The backend
  // decides from the facts whether it is a part supply.
  generateGroupInvoice: (orderIds, dueDate = null) => api.post(
    '/invoicing/generate-group',
    dueDate ? { order_ids: orderIds, due_date: dueDate } : { order_ids: orderIds },
  ),
  // D-INV4: tick an invoice paid (or put the tick back).
  markInvoicePaid: (invoiceId, paid = true) => api.post(`/invoicing/mark-paid/${invoiceId}`, { paid }),
  // WP-INV2 item A1: what the invoice WOULD carry, worked out by the same money
  // engine that raises it, writing nothing. Takes the identical body to
  // generateJobInvoice so the preview and the bill cannot disagree.
  previewJobInvoice: (jobId, invoiceData) => api.post(`/invoicing/preview/${jobId}`, invoiceData),
  getMonthlyReport: (month, year) => api.get('/invoicing/monthly-report', { params: { month, year } }),
  
  // Accounting Transactions
  getAccountingTransactions: () => api.get('/invoicing/accounting-transactions'),
  completeAccountingTransaction: (jobId, data = {}) => api.post(`/invoicing/complete-transaction/${jobId}`, data),
  retryXeroSync: (jobId) => api.post(`/invoicing/retry-xero-sync/${jobId}`),
  // Restores an archived order. Body is optional:
  //   { target_stage: "invoicing" | "delivery" | "finishing" | ... }
  //   { reverse_invoice: true | false }  (auto-decided if omitted)
  restoreArchivedJob: (jobId, body = {}) => api.post(`/invoicing/restore-job/${jobId}`, body),
  exportDraftedInvoicesCSV: () => api.get('/invoicing/export-drafted-csv', { responseType: 'blob' }),
  
  // Invoice Statement
  getOutstandingStatement: (clientId) => api.get(`/invoicing/outstanding-statement/${clientId}`),
  generateStatementPdf: (clientId) => api.get(`/invoicing/generate-statement-pdf/${clientId}`, { responseType: 'blob' }),
  
  // Email Templates
  getEmailTemplates: () => api.get('/email-templates'),
  createEmailTemplate: (template) => api.post('/email-templates', template),
  updateEmailTemplate: (templateId, template) => api.put(`/email-templates/${templateId}`, template),
  deleteEmailTemplate: (templateId) => api.delete(`/email-templates/${templateId}`),
  
  // Company Letterheads
  getLetterheads: () => api.get('/letterheads'),
  uploadLetterhead: (formData) => api.post('/letterheads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateLetterhead: (letterheadId, data) => api.put(`/letterheads/${letterheadId}`, null, { params: data }),
  deleteLetterhead: (letterheadId) => api.delete(`/letterheads/${letterheadId}`),
  setDefaultLetterhead: (letterheadId) => api.put(`/letterheads/${letterheadId}/set-default`),
  
  // Slitting & Material Calculators
  calculateSlittingPermutation: (data) => api.post('/calculators/slitting-permutation', data),
  getMaterialAvailability: (orderId) => api.get(`/orders/${orderId}/material-availability`),
  allocateMaterial: (orderId, data) => api.post(`/orders/${orderId}/allocate-material`, data),
  submitSlittingJob: (orderId, data) => api.post(`/orders/${orderId}/submit-slitting-job`, data),
  completeWindingJob: (orderId, data) => api.post(`/winding-jobs/${orderId}/complete`, data),
  
  // Quick Quotes
  getQuickQuotes: () => api.get('/quick-quotes'),
  getQuickQuote: (quoteId) => api.get(`/quick-quotes/${quoteId}`),
  createQuickQuote: (quoteData) => api.post('/quick-quotes', quoteData),
  updateQuickQuote: (quoteId, quoteData) => api.put(`/quick-quotes/${quoteId}`, quoteData),
  deleteQuickQuote: (quoteId) => api.delete(`/quick-quotes/${quoteId}`),
  convertQuickQuoteToClient: (quoteId, clientData) => api.post(`/quick-quotes/${quoteId}/convert-to-client`, clientData),
  addQuickQuoteToExistingClient: (quoteId, clientId) => api.post(`/quick-quotes/${quoteId}/add-to-existing-client`, { client_id: clientId }),
  revertClientToQuickQuote: (clientId) => api.post(`/clients/${clientId}/revert-to-quick-quote`),
  suggestQuoteAcronym: (company) => api.get('/quick-quotes/suggest-acronym', { params: { company } }),
  getMaterialHistory: (materialId) => api.get(`/materials/${materialId}/history`),
  getMaterialPriceHistory: (materialId) => api.get(`/materials/${materialId}/price-history`),
  traceSlitRoll: (slitRollId) => api.get(`/traceability/slit-roll/${encodeURIComponent(slitRollId)}`),
  traceOrderMaterials: (orderId) => api.get(`/traceability/order/${orderId}/material-trace`),
  getStorageLocations: () => api.get('/storage-locations'),
  createStorageLocation: (data) => api.post('/storage-locations', data),
  suggestStorageLocation: (data) => api.post('/storage-locations/suggest', data),
  generatePortalPasswordResetLink: (userId) => api.post(`/portal-users/${userId}/reset-link`),
  resetPortalPasswordWithToken: (token, newPassword) => api.post('/customer-portal/auth/reset-password', { token, new_password: newPassword }),
  acceptPortalQuote: (quoteId) => api.post(`/customer-portal/quotes/${quoteId}/accept`),
  getSupplierAttachments: (supplierId) => api.get(`/suppliers/${supplierId}/attachments`),
  uploadSupplierAttachment: (supplierId, formData) => api.post(`/suppliers/${supplierId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteSupplierAttachment: (supplierId, attId) => api.delete(`/suppliers/${supplierId}/attachments/${attId}`),
  generateSupplierAgreement: (supplierId) => api.get(`/suppliers/${supplierId}/generate-agreement`, { responseType: 'blob' }),
  updateSupplierAgreementStatus: (supplierId, status) => api.post(`/suppliers/${supplierId}/agreement-status`, { status }),
  generateClientTradeAccountEmailDraft: (clientId) =>
    api.get(`/clients/${clientId}/trade-account-email-draft`, { responseType: 'blob' }),
  logClientTradeAccountEmailSent: (clientId) =>
    api.post(`/clients/${clientId}/trade-account-email-draft/log-sent`),
  sendQuickQuote: (quoteId) => api.post(`/quick-quotes/${quoteId}/send`),
  generateQuotePDF: (quoteId, letterheadId) => api.get(`/quick-quotes/${quoteId}/pdf`, {
    params: letterheadId ? { letterhead_id: letterheadId } : {},
    responseType: 'blob'
  }),
  // Returns an .eml email-draft with the quote PDF attached, pre-addressed
  // to the customer. User opens the .eml in their default mail client.
  generateQuoteEmailDraft: (quoteId, letterheadId) => api.get(`/quick-quotes/${quoteId}/email-draft`, {
    params: letterheadId ? { letterhead_id: letterheadId } : {},
    responseType: 'blob'
  }),
  parseExcelForPaperCores: (formData) => api.post('/quick-quotes/parse-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Customer Portal Quote Requests (surfaced inside Quick Quotes)
  getPortalQuotePendingCount: () => api.get('/quick-quotes/portal/pending-count'),
  respondToPortalQuote: (quoteId, data) => api.post(`/quick-quotes/portal/${quoteId}/respond`, data),
  deletePortalQuote: (quoteId) => api.delete(`/quick-quotes/portal/${quoteId}`),
  markPortalQuoteAmendmentReviewed: (quoteId) => api.post(`/quick-quotes/portal/${quoteId}/mark-amendment-reviewed`),

  // Customer Portal Delivery Requests (sidebar indicator on Orders)
  getPortalDeliveryRequestPendingCount: () => api.get('/portal-delivery-requests/pending-count'),

  // Customer Portal Orders (dashboard counter)
  getPortalOrdersPendingCount: () => api.get('/portal-orders/pending-count'),

  // Stock Reconciliation (main-app manager view + indefinite undo)
  listStockReconciliations: (params) => api.get('/stock-reconciliations', { params }),
  getStockReconciliation: (id) => api.get(`/stock-reconciliations/${id}`),
  undoStockReconciliation: (id, notes) => api.post(`/stock-reconciliations/${id}/undo`, { notes }),

  // Incoming Stock (main app — staff manage batches, mark arrived, etc.)
  uploadIncomingStockExcel: (holdingId, formData) =>
    api.post(`/customer-stock-holdings/${holdingId}/upload-incoming-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  listIncomingBatches: (holdingId) =>
    api.get(`/customer-stock-holdings/${holdingId}/incoming-batches`),
  amendIncomingBatchDate: (holdingId, batchId, expectedDate) =>
    api.put(`/customer-stock-holdings/${holdingId}/incoming-batches/${batchId}/expected-date`, {
      expected_delivery_date: expectedDate
    }),
  markIncomingBatchArrived: (holdingId, batchId, actualArrivalDate) =>
    api.post(`/customer-stock-holdings/${holdingId}/incoming-batches/${batchId}/mark-arrived`, {
      actual_arrival_date: actualArrivalDate
    }),
  deleteIncomingBatch: (holdingId, batchId) =>
    api.delete(`/customer-stock-holdings/${holdingId}/incoming-batches/${batchId}`),

  // Incoming Delivery Alerts
  getIncomingDeliveryAlerts: () => api.get('/incoming-delivery-alerts'),
  confirmIncomingDeliveryAlert: (batchId, data) => api.post(`/incoming-delivery-alerts/${batchId}/confirm`, data),

  // Client Attachments
  getClientAttachments: (clientId, params) =>
    api.get(`/clients/${clientId}/attachments`, params ? { params } : undefined),
  uploadClientAttachment: (clientId, formData) => api.post(`/clients/${clientId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadClientAttachment: (clientId, attachmentId) => api.get(`/clients/${clientId}/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  }),
  deleteClientAttachment: (clientId, attachmentId) => api.delete(`/clients/${clientId}/attachments/${attachmentId}`),

  // WP-CC (18/09/2026) — customer consumption: what a customer said they
  // would use each year, against what we have actually invoiced them.
  // The price lists and consumption forecasts on a customer, for the
  // "where did this figure come from?" picker.
  getClientSourceDocuments: (clientId) =>
    api.get(`/clients/${clientId}/attachments/source-documents`),
  getClientProductUsage: (clientId) => api.get(`/clients/${clientId}/product-usage`),
  getClientProductUsageOne: (clientId, productId) =>
    api.get(`/clients/${clientId}/product-usage/${productId}`),
  saveClientProductUsage: (clientId, productId, data) =>
    api.put(`/clients/${clientId}/product-usage/${productId}`, data),
  deleteClientProductUsage: (clientId, productId) =>
    api.delete(`/clients/${clientId}/product-usage/${productId}`),
  getClientProductConsumption: (clientId, productId) =>
    api.get(`/clients/${clientId}/products/${productId}/consumption`),
  // The Consumption Check report. Admin and manager only, the same as Login
  // Attempts — deliberately narrower than the Reports screen itself.
  getConsumptionCheck: (params) => api.get('/reports/consumption-check', { params }),
  exportConsumptionCheckCsv: (params) =>
    api.get('/reports/consumption-check.csv', { params, responseType: 'blob' }),
  getConsumptionCheckPdf: (params) =>
    api.get('/reports/consumption-check.pdf', { params, responseType: 'blob' }),
  // NOTE: `GET /reports/consumption-reviews` also exists on the server and has
  // no helper here on purpose — no screen lists the automatic reviews, because
  // each one already arrives as a task and is filed in the customer's Files.
  // It is there so a person can check what the nightly job has produced.


  // D-SS10 (17/09/2026) — the Login Attempts report. Admin and manager only;
  // deliberately narrower than the Reports screen itself, which a sales user
  // can open.
  getLoginAttempts: (params) => api.get('/reports/login-attempts', { params }),
  exportLoginAttemptsCsv: (params) =>
    api.get('/reports/login-attempts.csv', { params, responseType: 'blob' }),

  // D-SS3 (17/09/2026) — what the privilege ticks WOULD have refused during
  // the watch week. Admin only.
  getPermissionWatch: () => api.get('/reports/permission-watch'),
  clearPermissionWatch: () => api.delete('/reports/permission-watch'),

  // Xero Integration
  checkXeroConnection: () => api.get('/xero/status'),
  getXeroDebugInfo: () => api.get('/xero/debug'),
  getXeroAuthUrl: () => api.get('/xero/auth/url'),
  handleXeroCallback: (data) => api.post('/xero/auth/callback', data),
  disconnectXero: () => api.delete('/xero/disconnect'),
  getNextXeroInvoiceNumber: () => api.get('/xero/next-invoice-number'),
  createXeroDraftInvoice: (invoiceData) => api.post('/xero/create-draft-invoice', invoiceData),
  getXeroAccountCodes: () => api.get('/xero/account-codes'),
  getXeroTaxRates: () => api.get('/xero/tax-rates'),
  
  // Invoice Settings
  getInvoiceSettings: () => api.get('/invoicing/settings'),
  updateInvoiceSettings: (settings) => api.put('/invoicing/settings', settings),
  
  // User Management
  // D-SS5 (17/09/2026): removed staff are hidden from every list. Only Staff
  // & Security asks for them, through its "Show deleted" tick, and only an
  // admin is given them.
  getUsers: ({ includeDeleted = false } = {}) =>
    api.get(includeDeleted ? '/users?include_deleted=true' : '/users'),
  createUser: (userData) => api.post('/users', userData),
  getUser: (userId) => api.get(`/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  changePassword: (passwordData) => api.post('/users/change-password', passwordData),
  // WP-SS1 — admin account recovery for staff logins.
  resetUserPassword: (userId, data) => api.post(`/users/${userId}/reset-password`, data),
  unlockUser: (userId) => api.post(`/users/${userId}/unlock`),
  resetUser2FA: (userId) => api.post(`/users/${userId}/reset-2fa`),
  // D-SS5 — bring a removed staff member back. They return INACTIVE.
  restoreUser: (userId) => api.post(`/users/${userId}/restore`),

  // WP-2E — "Not now" on the 2FA set-up prompt. The server counts the clear
  // and locks the account on the third one.
  dismiss2FASetup: () => api.post('/auth/2fa/dismiss-setup'),

  // Task Reminders
  // ── Tasks ──────────────────────────────────────────────────────────
  createTask: (data) => api.post('/tasks', data),
  getMyTasks: () => api.get('/tasks/mine'),
  getAllTasks: () => api.get('/tasks'),
  getTasksInbox: () => api.get('/tasks/inbox'),
  getTasksSent: () => api.get('/tasks/sent'),
  getTasksArchived: () => api.get('/tasks/archived'),
  getTasksActionRequiredCount: () => api.get('/tasks/action-required-count'),
  getTaskDetail: (id) => api.get(`/tasks/${id}`),
  markTaskSeen: (id) => api.post(`/tasks/${id}/seen`),
  updateTask: (id, data) => api.put(`/tasks/${id}`, data),
  setTaskStatus: (id, data) => api.post(`/tasks/${id}/status`, data),
  completeTask: (id) => api.post(`/tasks/${id}/complete`),
  archiveTask: (id) => api.post(`/tasks/${id}/archive`),
  unarchiveTask: (id) => api.post(`/tasks/${id}/unarchive`),
  addTaskComment: (id, data) => api.post(`/tasks/${id}/comments`, data),
  forwardTask: (id, data) => api.post(`/tasks/${id}/forward`, data),
  uploadTaskAttachment: (id, formData) => api.post(`/tasks/${id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTaskAttachment: (id, attachmentId) => api.get(`/tasks/${id}/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  }),
  deleteTaskAttachment: (id, attachmentId) => api.delete(`/tasks/${id}/attachments/${attachmentId}`),
  deleteTask: (id) => api.delete(`/tasks/${id}`),

  // Fail Logging
  getFailLogs: (filters) => api.get('/fail-logs', { params: filters }),
  getFailLog: (logId) => api.get(`/fail-logs/${logId}`),
  createFailLog: (logData) => api.post('/fail-logs', logData),
  updateFailLog: (logId, updateData) => api.put(`/fail-logs/${logId}`, updateData),
  deleteFailLog: (logId) => api.delete(`/fail-logs/${logId}`),

  // Error Logs (auto-captured + manual, unified in the Issues viewer)
  getErrorLogs: (params) => api.get('/error-logs', { params }),
  acknowledgeErrorLog: (logId) => api.post(`/error-logs/${logId}/acknowledge`),
  deleteErrorLog: (logId) => api.delete(`/error-logs/${logId}`),
  bulkDeleteErrorLogs: (data) => api.post('/error-logs/bulk-delete', data),
  
  // Printer Configuration
  getPrinterAssignments: () => api.get('/printers/assignments'),
  savePrinterAssignment: (assignmentData) => api.post('/printers/assignments', assignmentData),
  getAvailablePrinters: () => api.get('/printers/available'),
  addCustomPrinter: (printerData) => api.post('/printers/custom', printerData),
  updateCustomPrinter: (printerName, printerData) => api.put(`/printers/custom/${encodeURIComponent(printerName)}`, printerData),
  removeCustomPrinter: (printerName) => api.post('/printers/custom/delete', { printer_name: printerName }),
  getPrinterForFunction: (functionId) => api.get(`/printers/assignment/${functionId}`),
  testPrinterConnection: (printerName) => api.post(`/printers/test/${encodeURIComponent(printerName)}`),
  discoverNetworkPrinters: () => api.get('/printers/discover'),
  getSystemPrinters: () => api.get('/printers/system'),
  testNetworkPrinter: (data) => api.post('/printers/test-network', data),
  printTestPageToNetworkPrinter: (data) => api.post('/printers/print-test-page', data),
  // Universal test print — works for CUPS / USB / system printers AND
  // network-IP thermal printers. Body: { printer_name }.
  testPrintPrinter: (data) => api.post('/printers/test-print', data),
  getAllTemplates: () => api.get('/printers/templates'),
  getPrinterSettings: () => api.get('/printers/settings'),
  
  // Machinery Rates
  getMachineryRates: () => api.get('/machinery-rates'),
  createMachineryRate: (data) => api.post('/machinery-rates', data),
  getMachineryRate: (id) => api.get(`/machinery-rates/${id}`),
  updateMachineryRate: (id, data) => api.put(`/machinery-rates/${id}`, data),
  deleteMachineryRate: (id) => api.delete(`/machinery-rates/${id}`),

  // Stock Management System
  // Raw Substrates
  getRawSubstrates: (clientId = null) => api.get(`/stock/raw-substrates${clientId ? `?client_id=${clientId}` : ''}`),
  createRawSubstrate: (data) => api.post('/stock/raw-substrates', data),
  updateRawSubstrate: (id, data) => api.put(`/stock/raw-substrates/${id}`, data),
  deleteRawSubstrate: (id) => api.delete(`/stock/raw-substrates/${id}`),

  // Raw Materials Stock
  getRawMaterialsStock: () => api.get('/stock/raw-materials'),
  createRawMaterialStock: (data) => api.post('/stock/raw-materials', data),
  updateRawMaterialStock: (id, data) => api.put(`/stock/raw-materials/${id}`, data),
  deleteRawMaterialStock: (id) => api.delete(`/stock/raw-materials/${id}`),

  // Stock Movements
  getStockMovements: (stockId) => api.get(`/stock/movements/${stockId}`),

  // Stock Alerts
  getStockAlerts: () => api.get('/stock/alerts'),
  acknowledgeStockAlert: (id, data) => api.post(`/stock/alerts/${id}/acknowledge`, data),
  checkLowStock: () => api.post('/stock/check-low-stock'),

  // Slit Width Management
  getSlitWidthsByMaterial: (materialId) => api.get(`/slit-widths/material/${materialId}`),
  createSlitWidth: (data) => api.post('/slit-widths', data),
  updateSlitWidth: (slitWidthId, data) => api.put(`/slit-widths/${slitWidthId}`, data),
  deleteSlitWidth: (slitWidthId) => api.delete(`/slit-widths/${slitWidthId}`),
  checkSlitWidthAvailability: (materialId, widthMm, quantityMeters) => 
    api.get(`/slit-widths/check-availability?material_id=${materialId}&required_width_mm=${widthMm}&required_quantity_meters=${quantityMeters}`),
  allocateSlitWidth: (allocationData) => api.post('/slit-widths/allocate', allocationData),
  getSlitWidthAllocations: (orderId) => api.get(`/slit-widths/allocations/${orderId}`),

  // Label Designer
  getLabelTemplates: () => api.get('/label-templates'),
  getLabelTemplate: (templateId) => api.get(`/label-templates/${templateId}`),
  createLabelTemplate: (data) => api.post('/label-templates', data),
  updateLabelTemplate: (templateId, data) => api.put(`/label-templates/${templateId}`, data),
  deleteLabelTemplate: (templateId) => api.delete(`/label-templates/${templateId}`),
  ensureMobilePalletTemplate: () => api.post('/label-templates/ensure-mobile-pallet'),
  
  // Page Designer
  getPageTemplates: () => api.get('/page-templates'),
  getPageTemplate: (templateId) => api.get(`/page-templates/${templateId}`),
  createPageTemplate: (data) => api.post('/page-templates', data),
  updatePageTemplate: (templateId, data) => api.put(`/page-templates/${templateId}`, data),
  deletePageTemplate: (templateId) => api.delete(`/page-templates/${templateId}`),
  
  // Printing
  getPrinters: () => api.get('/printers'),
  printLabel: (data) => api.post('/print-label', data),
  
  // Pallet Label Printing
  printPalletLabels: (holdingId, data) => api.post(`/customer-stock-holdings/${holdingId}/print-labels`, data),

  // Portal Delivery Request Management (Staff)
  getPortalDeliveryRequests: (params) => api.get('/portal-delivery-requests', { params }),
  confirmDeliveryRequest: (requestId, data) => api.post(`/portal-delivery-requests/${requestId}/confirm`, data),
  confirmDeliveryDate: (requestId, data) => api.post(`/portal-delivery-requests/${requestId}/confirm-delivery-date`, data),
  adjustDeliveryDate: (requestId, data) => api.post(`/portal-delivery-requests/${requestId}/adjust-delivery-date`, data),
  adjustOrderDeliveryDate: (orderId, data) => api.post(`/orders/${orderId}/adjust-delivery-date`, data),
  completeDeliveryRequest: (requestId, data) => api.post(`/portal-delivery-requests/${requestId}/complete`, data),
  markPalletsDelivered: (requestId, data) => api.post(`/portal-delivery-requests/${requestId}/mark-delivered`, data),
  archiveDeliveryRequest: (requestId) => api.post(`/portal-delivery-requests/${requestId}/archive`),
  approveDeleteRequest: (requestId) => api.post(`/portal-delivery-requests/${requestId}/approve-delete`),
  denyDeleteRequest: (requestId, data) => api.post(`/portal-delivery-requests/${requestId}/deny-delete`, data),
  getOrderConfirmationPDF: (requestId) => api.get(`/portal-delivery-requests/${requestId}/order-confirmation-pdf`, { responseType: 'blob' }),
  getDeliveryRequestDetailsPDF: (requestId) => api.get(`/portal-delivery-requests/${requestId}/details-pdf`, { responseType: 'blob' }),

  // WP-BG — run groups on the production board (jobs being MADE together in
  // one setup). Not the customer order group on Invoicing, which is about jobs
  // being BILLED together, and not the delivery grouping below, which collects
  // pallet deliveries for one customer. Three words, three things.
  //
  // The PIN always travels in the BODY. It is never put in a URL, a query
  // string or a toast — a PIN in an address is in the browser history, the web
  // server log and every proxy in between.
  createBoardGroup: (members, pin) => api.post('/production/board-groups', { members, pin }),
  getBoardGroup: (groupId) => api.get(`/production/board-groups/${groupId}`),
  addBoardGroupMember: (groupId, member, pin) =>
    api.post(`/production/board-groups/${groupId}/members`, { member, pin }),
  moveBoardGroup: (groupId, data) => api.post(`/production/board-groups/${groupId}/move`, data),
  // A POST rather than a DELETE with a body: a request body on a DELETE is
  // legal but is the first thing a reverse proxy drops, and this one carries
  // the PIN.
  ungroupBoardGroup: (groupId, pin) =>
    api.post(`/production/board-groups/${groupId}/ungroup`, { pin }),

  // WP-BG — the manager PIN itself (Staff & Security, and My Account).
  setUserPin: (userId, pin, currentPassword) =>
    api.put(`/users/${userId}/pin`, { pin, current_password: currentPassword }),
  // Clearing your OWN PIN carries your password, the same confirmation setting
  // one asks for; an admin clearing somebody else's sends nothing.
  clearUserPin: (userId, currentPassword) => api.delete(`/users/${userId}/pin`,
    currentPassword ? { data: { current_password: currentPassword } } : undefined),

  // Delivery Groups
  createDeliveryGroup: (deliveryRequestIds) => api.post('/delivery-groups', { delivery_request_ids: deliveryRequestIds }),
  deleteDeliveryGroup: (groupId) => api.delete(`/delivery-groups/${groupId}`),
  getDeliveryGroup: (groupId) => api.get(`/delivery-groups/${groupId}`),
  completeDeliveryGroup: (groupId) => api.post(`/delivery-groups/${groupId}/complete`),
  completeSingleDelivery: (requestId) => api.post(`/delivery-requests/${requestId}/complete-delivery`),
  getPackingSlip: (requestId) => api.get(`/delivery-requests/${requestId}/packing-slip`),
  getGroupPackingSlip: (groupId) => api.get(`/delivery-groups/${groupId}/packing-slip`),

  // Order Delete/Change Request Management (Staff)
  approveOrderDelete: (orderId) => api.post(`/orders/${orderId}/approve-delete`),
  denyOrderDelete: (orderId, data) => api.post(`/orders/${orderId}/deny-delete`, data),
  approveOrderChange: (orderId) => api.post(`/orders/${orderId}/approve-change`),
  denyOrderChange: (orderId, data) => api.post(`/orders/${orderId}/deny-change`, data),

  // Portal Orders Management (Staff view of customer portal orders)
  getPortalOrders: (status, clientId) => api.get('/portal-orders', { params: { status, client_id: clientId } }),
  confirmPortalOrder: (orderId, data) => api.post(`/portal-orders/${orderId}/confirm`, data),
  rejectPortalOrder: (orderId, data) => api.post(`/portal-orders/${orderId}/reject`, data),
  deletePortalOrder: (orderId) => api.delete(`/portal-orders/${orderId}`),
  deletePortalDeliveryRequest: (requestId) => api.delete(`/portal-delivery-requests/${requestId}`),

  // Archived Orders
  getArchivedOrders: (params) => api.get('/archived-orders', { params }),
  getClientArchivedOrders: (clientId, params) => api.get(`/clients/${clientId}/archived-orders`, { params }),
  exportArchivedOrdersCsv: (params) => api.get('/archived-orders/export-csv', { params, responseType: 'blob' }),

  // Trade Account Application — blank template download
  downloadTradeAccountApplication: () => api.get('/forms/trade-account-application', { responseType: 'blob' }),

  // Surcharges (Fuel / Freight / Custom)
  listSurcharges: (includeEnded = false) => api.get('/surcharges', { params: { include_ended: includeEnded } }),
  createSurcharge: (data) => api.post('/surcharges', data),
  updateSurcharge: (id, data) => api.put(`/surcharges/${id}`, data),
  endSurcharge: (id) => api.post(`/surcharges/${id}/end`),
  addClientsToSurcharge: (id, clientIds) => api.post(`/surcharges/${id}/add-clients`, { client_ids: clientIds }),
  previewSurcharges: (clientId, subtotal) => api.get(`/surcharges/preview/${clientId}`, { params: { subtotal } }),
  generateSurchargePdfs: (id) => api.post(`/surcharges/${id}/generate-pdfs`),
  downloadSurchargePdfsZip: (id) => api.get(`/surcharges/${id}/pdfs.zip`, { responseType: 'blob' }),

  // Generic GET/POST/PUT methods for flexibility. The second argument is
  // forwarded to axios as the request config — { params, headers,
  // responseType, ... } — so callers can pass query params, set blob
  // response types, etc. without bypassing the helper.
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  delete: (url, config) => api.delete(url, config),
};

// File download helper
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(amount);
};

// Format a date the house way: DD/MM/YYYY, no time (D-DT1, 13/09/2026).
// Every date a person reads on any screen comes through here, so the whole app
// shows the same shape. Returns an empty string for anything that isn't a real
// date, so callers can drop it straight into a sentence without guarding.
export const formatDate = (date) => {
  if (!date) return '';
  // A plain YYYY-MM-DD string is a calendar date, not an instant: read it
  // straight off the text so a timezone cannot shift it by a day.
  if (typeof date === 'string') {
    const parts = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (parts) return `${parts[3]}/${parts[2]}/${parts[1]}`;
  }
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()}`;
};

// Format a date and time the house way: DD/MM/YYYY HH:mm (24 hour clock).
// Returns an empty string for anything that isn't a real date, so callers can
// drop it straight into a sentence without guarding.
export const formatDateTime = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()} ` +
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

// Kept as a name only: the kiosk and its tests import this. `formatDate` is now
// the house DD/MM/YYYY formatter, so this is simply another name for it.
export const formatDateDDMMYYYY = formatDate;

// Format date for input
export const formatDateForInput = (date) => {
  return new Date(date).toISOString().split('T')[0];
};

// Production stage display names
export const stageDisplayNames = {
  order_entered: 'Order Entered',
  pending_material: 'Pending Material',
  paper_slitting: 'Paper Slitting',
  winding: 'Winding',
  finishing: 'Finishing',
  delivery: 'Delivery',
  invoicing: 'Invoicing',
  cleared: 'Cleared'
};

// Stage colors
export const stageColors = {
  order_entered: 'bg-blue-900 border-blue-700',
  pending_material: 'bg-orange-900 border-orange-700',
  paper_slitting: 'bg-purple-900 border-purple-700',
  winding: 'bg-indigo-900 border-indigo-700',
  finishing: 'bg-green-900 border-green-700',
  delivery: 'bg-teal-900 border-teal-700',
  invoicing: 'bg-yellow-900 border-yellow-700'
};

// User role display names
export const roleDisplayNames = {
  admin: 'Administrator',
  production_manager: 'Production Manager',
  sales: 'Sales',
  production_team: 'Production Team'
};

// ============= ARCHIVED ORDERS API =============

// Get archived orders for a client
export const getClientArchivedOrders = async (clientId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.search_query) params.append('search_query', filters.search_query);
  
  const queryString = params.toString();
  const url = queryString ? 
    `/clients/${clientId}/archived-orders?${queryString}` : 
    `/clients/${clientId}/archived-orders`;
    
  return api.get(url);
};

// Generate Fast Report for archived orders
export const generateFastReport = async (clientId, reportData) => {
  return api.post(`/clients/${clientId}/archived-orders/fast-report`, reportData, {
    responseType: 'blob' // Important for file downloads
  });
};

// Helper function to download Excel file
export const downloadExcelReport = async (clientId, reportData) => {
  try {
    const response = await generateFastReport(clientId, reportData);
    
    // Create blob link to download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'archived_orders_report.xlsx';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
  } catch (error) {
    console.error('Error downloading Excel report:', error);
    throw error;
  }
};

// Database Backup API helpers
export const backupHelpers = {
  // Get list of all backups
  listBackups: () => api.get('/backups'),
  
  // Create a new backup
  createBackup: (data = {}) => api.post('/backups', data),
  
  // Restore from a backup
  restoreBackup: (backupName) => api.post(`/backups/${backupName}/restore`),
  
  // Delete a backup
  deleteBackup: (backupName) => api.delete(`/backups/${backupName}`),
  
  // Download backup as ZIP
  downloadBackup: async (backupName) => {
    const response = await api.get(`/backups/${backupName}/download`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${backupName}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  },
  
  // Get backup settings
  getSettings: () => api.get('/backups/settings'),
  
  // Update backup settings
  updateSettings: (data) => api.put('/backups/settings', data),
};